import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Coins, Loader2 } from 'lucide-react';

import api from '../../lib/api';
import { fmtCurrency } from '../../lib/localeUtils';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../ui/dialog';

// Asked once per shift, the first time the cashier opens the POS, so the
// expected drawer at handover includes the cash they started with.
export const OpeningFloatDialog = () => {
  const [summary, setSummary] = useState(null);
  const [amount, setAmount] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let live = true;
    api.get('/shift/summary')
      .then(({ data }) => {
        if (!live || data.float_set) return;
        setSummary(data);
        setAmount(String(data.suggested_float || 0));
      })
      .catch(() => {});
    return () => { live = false; };
  }, []);

  const save = async (value) => {
    setBusy(true);
    try {
      await api.post('/shift/float', { amount: Number(value) || 0 });
      toast.success(`Shift started with ${fmtCurrency(Number(value) || 0)} in the drawer`);
      setSummary(null);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not save the opening float');
    }
    setBusy(false);
  };

  if (!summary) return null;

  return (
    <Dialog open onOpenChange={() => {}}>
      <DialogContent className="max-w-md" data-testid="opening-float-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Coins className="h-4 w-4 text-primary" /> Opening float
          </DialogTitle>
          <DialogDescription>
            How much cash is in {summary.terminal_label || 'this till'} before you start selling?
            It is added to what we expect at handover.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          <div className="space-y-1.5">
            <Label className="text-xs">Cash in the drawer now</Label>
            <Input
              inputMode="decimal" autoFocus value={amount}
              onChange={(e) => setAmount(e.target.value.replace(/[^\d.]/g, ''))}
              data-testid="opening-float-input"
            />
          </div>
          {summary.suggested_float > 0 && (
            <p className="text-xs text-muted-foreground" data-testid="opening-float-suggestion">
              The last shift here was handed over with {fmtCurrency(summary.suggested_float)} counted.
            </p>
          )}
        </div>

        <div className="flex justify-end gap-2 pt-2">
          <Button variant="ghost" onClick={() => save(0)} disabled={busy} data-testid="opening-float-none">
            No float
          </Button>
          <Button onClick={() => save(amount)} disabled={busy || amount === ''} className="gap-2" data-testid="opening-float-save">
            {busy && <Loader2 className="h-4 w-4 animate-spin" />} Start shift
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
